import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image, ProgressBar,
  DropdownButton, Dropdown, InputGroup, Tabs, Tab,
  Accordion, ListGroup, Carousel, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';
import {
  FaBuilding, FaHammer, FaLaptopCode, FaServer,
  FaIndustry, FaUserTie, FaRoad, FaCube,
  FaEdit, FaTrash, FaEye, FaStar, FaCalendarAlt,
  FaMapMarkerAlt, FaUsers, FaChartLine, FaCogs,
  FaImage, FaUpload, FaTimes, FaPlus, FaSearch,
  FaFilter, FaFileDownload, FaChartBar, FaMoneyBill,
  FaPhone, FaEnvelope, FaUser, FaClock, FaCheckCircle,
  FaPauseCircle, FaTimesCircle, FaCalendarCheck, FaCalendarPlus
} from 'react-icons/fa';

const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewingProject, setViewingProject] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Options
  const [options, setOptions] = useState({
    project_statuses: [],
    project_types: [],
    currencies: []
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    project_type: '',
    project_status: '',
    is_featured: '',
    is_active: ''
  });
  
  // Statistics
  const [statistics, setStatistics] = useState(null);
  const [activeTab, setActiveTab] = useState('projects');
  
  const [formData, setFormData] = useState({
    project_code: '',
    project_name: '',
    project_type: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    short_description: '',
    detailed_description: '',
    location: '',
    budget: '',
    currency: 'USD',
    start_date: '',
    end_date: '',
    project_status: 'Planned',
    progress_percentage: 0,
    project_manager: '',
    team_size: '',
    technologies_used: [],
    materials_used: [],
    thumbnail_image: '',
    gallery_images: [],
    is_featured: false,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchOptions();
    fetchStatistics();
  }, []);
  
  useEffect(() => {
    fetchProjects();
  }, [filters]);
  
  // DATE FORMATTING FUNCTIONS
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === 'null') {
      return 'N/A';
    }
    
    try {
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        date = new Date(year, month - 1, day);
      } else {
        return 'Invalid Date';
      }
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'Input:', dateString);
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === '0000-00-00 00:00:00' || dateString === 'null') {
      return 'N/A';
    }
    
    try {
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString.replace(' ', 'T'));
      }
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === 'null') {
      return '';
    }
    
    try {
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        date = new Date(year, month - 1, day);
      } else {
        return '';
      }
      
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };
  
  const resetForm = () => {
    setFormData({
      project_code: '',
      project_name: '',
      project_type: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      short_description: '',
      detailed_description: '',
      location: '',
      budget: '',
      currency: 'USD',
      start_date: '',
      end_date: '',
      project_status: 'Planned',
      progress_percentage: 0,
      project_manager: '',
      team_size: '',
      technologies_used: [],
      materials_used: [],
      thumbnail_image: '',
      gallery_images: [],
      is_featured: false,
      is_active: true
    });
    setThumbnailPreview(null);
    setGalleryPreviews([]);
  };
  
  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };
  
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      let url = `${API_BASE_URL}/projects.php?admin=true`;
      
      // Add filters to URL
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.project_type) params.append('project_type', filters.project_type);
      if (filters.project_status) params.append('project_status', filters.project_status);
      if (filters.is_featured !== '') params.append('is_featured', filters.is_featured);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);
      
      if (params.toString()) {
        url += '&' + params.toString();
      }
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedProjects = response.data.data.map(project => {
          return {
            ...project,
            id: parseInt(project.id) || 0,
            budget: parseFloat(project.budget) || 0,
            progress_percentage: parseInt(project.progress_percentage) || 0,
            team_size: parseInt(project.team_size) || 0,
            is_featured: project.is_featured === "1" || project.is_featured === 1 || project.is_featured === true,
            is_active: project.is_active === "1" || project.is_active === 1 || project.is_active === true,
            technologies_used: Array.isArray(project.technologies_used) ? 
                              project.technologies_used.filter(t => t) : 
                              (typeof project.technologies_used === 'string' ? 
                               project.technologies_used.split(',').filter(t => t.trim()) : []),
            materials_used: Array.isArray(project.materials_used) ? 
                           project.materials_used.filter(m => m) : 
                           (typeof project.materials_used === 'string' ? 
                            project.materials_used.split(',').filter(m => m.trim()) : []),
            gallery_images: Array.isArray(project.gallery_images) ? 
                           project.gallery_images.filter(img => img) : 
                           (typeof project.gallery_images === 'string' ? 
                            project.gallery_images.split(',').filter(img => img.trim()) : []),
            start_date: project.start_date || null,
            end_date: project.end_date || null,
            created_at: project.created_at || null,
            updated_at: project.updated_at || null
          };
        });
        
        setProjects(processedProjects);
        setFilteredProjects(processedProjects);
      } else {
        setError('Failed to load projects');
      }
    } catch (err) {
      console.error('Fetch projects error:', err);
      setError('Error connecting to server: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/projects.php?options=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setOptions(response.data.data || {
          project_statuses: [],
          project_types: [],
          currencies: []
        });
      }
    } catch (err) {
      console.error('Error fetching options:', err);
      setOptions({
        project_statuses: ['Planned', 'Ongoing', 'On Hold', 'Completed', 'Cancelled'],
        project_types: ['Real Estate', 'Construction', 'Software Development', 'IT Services', 'Manufacturing', 'Consulting', 'Infrastructure', 'Other'],
        currencies: ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD']
      });
    } finally {
      setOptionsLoading(false);
    }
  };
  
  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/projects.php?stats=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'project_name' && !formData.project_code && !editingProject) {
      const projectCode = generateProjectCode(value, formData.project_type);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        project_code: projectCode
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };
  
  const generateProjectCode = (projectName, projectType) => {
    const prefix = projectType ? projectType.substring(0, 3).toUpperCase().replace(/\s/g, '') : 'PRJ';
    const dateCode = new Date().toISOString().slice(0, 7).replace('-', '');
    const randomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${prefix}-${dateCode}-${randomCode}`;
  };
  
  const handleArrayFieldChange = (field, value) => {
    const values = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      [field]: values
    }));
  };
  
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setFormData(prev => ({
        ...prev,
        thumbnail_file: file,
        thumbnail_image: '' // Clear existing thumbnail path if uploading new
      }));
    }
  };
  
  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = [];
    const newFiles = [];
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        if (newPreviews.length === files.length) {
          setGalleryPreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
      newFiles.push(file);
    });
    
    setFormData(prev => ({
      ...prev,
      gallery_files: [...(prev.gallery_files || []), ...newFiles]
    }));
  };
  
  const removeThumbnail = () => {
    setThumbnailPreview(null);
    setFormData(prev => ({
      ...prev,
      thumbnail_image: '',
      thumbnail_file: null
    }));
  };
  
  const removeGalleryImage = (index) => {
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      gallery_files: prev.gallery_files ? prev.gallery_files.filter((_, i) => i !== index) : [],
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };
  
  const uploadImage = async (file, type) => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);
    
    // Add timestamp to avoid cache
    const timestamp = Date.now();
    
    const response = await axios.post(`${API_BASE_URL}/upload_project_image.php?t=${timestamp}`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      timeout: 30000 // 30 second timeout
    });
    
    console.log('Upload response:', response.data);
    
    if (response.data.success) {
      // Try different possible response formats
      return response.data.image_url || 
             response.data.file_path || 
             response.data.filePath || 
             response.data.url ||
             response.data.filename;
    } else {
      console.error('Upload failed:', response.data.message);
      return null;
    }
  } catch (err) {
    console.error('Image upload error:', err);
    if (err.response) {
      console.error('Response data:', err.response.data);
      console.error('Response status:', err.response.status);
    }
    return null;
  }
};
  
  const handleSubmit = async (e) => {
  e.preventDefault();
  
  try {
    setUploading(true);
    setError('');
    setSuccess('');
    
    // Prepare data for API
    let dataToSend = { ...formData };
    
    // First upload thumbnail if exists
    if (formData.thumbnail_file) {
      console.log('Uploading new thumbnail...');
      const uploadedUrl = await uploadImage(formData.thumbnail_file, 'thumbnail');
      if (uploadedUrl) {
        dataToSend.thumbnail_image = uploadedUrl;
      } else {
        setError('Failed to upload thumbnail');
        setUploading(false);
        return;
      }
    }
    
    // Upload new gallery images
    let finalGalleryImages = [];
    
    // Add existing gallery images (if editing)
    if (formData.gallery_images && Array.isArray(formData.gallery_images)) {
      // Filter out any empty strings or null values
      finalGalleryImages = [...formData.gallery_images.filter(img => img && img.trim())];
    }
    
    // Upload new gallery images
    if (formData.gallery_files && formData.gallery_files.length > 0) {
      console.log('Uploading new gallery images...');
      for (const file of formData.gallery_files) {
        const uploadedUrl = await uploadImage(file, 'gallery');
        if (uploadedUrl) {
          finalGalleryImages.push(uploadedUrl);
        }
      }
    }
    
    // Handle technologies and materials - convert to comma-separated strings
    if (Array.isArray(dataToSend.technologies_used)) {
      dataToSend.technologies_used = dataToSend.technologies_used
        .filter(item => item && item.trim())
        .join(',');
    }
    
    if (Array.isArray(dataToSend.materials_used)) {
      dataToSend.materials_used = dataToSend.materials_used
        .filter(item => item && item.trim())
        .join(',');
    }
    
    // Handle gallery images - convert to JSON string
    dataToSend.gallery_images = JSON.stringify(finalGalleryImages);
    
    // Remove file objects from dataToSend
    delete dataToSend.thumbnail_file;
    delete dataToSend.gallery_files;
    
    console.log('Sending data to API:', dataToSend);
    
    const token = localStorage.getItem('token');
    let response;
    
    if (editingProject) {
      console.log('Updating project ID:', editingProject.id);
      response = await axios.put(
        `${API_BASE_URL}/projects.php?id=${editingProject.id}`,
        dataToSend,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } else {
      console.log('Creating new project...');
      response = await axios.post(`${API_BASE_URL}/projects.php`, dataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    }
    
    console.log('API Response:', response.data);
    
    if (response.data.success) {
      setSuccess(editingProject ? 'Project updated successfully!' : 'Project added successfully!');
      setShowModal(false);
      resetForm();
      setEditingProject(null);
      fetchProjects();
      fetchStatistics();
      
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError('Operation failed: ' + (response.data.message || 'Unknown error'));
      console.error('API Error details:', response.data);
    }
  } catch (err) {
    console.error('Submit error details:', err);
    setError('Error saving project: ' + 
      (err.response?.data?.message || 
       err.response?.data?.error || 
       err.message || 
       'Unknown error'));
  } finally {
    setUploading(false);
  }
};
  
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project? This will also delete all associated images.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/projects.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Project deleted!');
          fetchProjects();
          fetchStatistics();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete project: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting project');
        console.error('Delete error:', err);
      }
    }
  };
  
  const handleEdit = (project) => {
  setEditingProject(project);
  
  // Parse arrays properly
  const technologies = Array.isArray(project.technologies_used) ? 
    project.technologies_used.filter(t => t) : 
    (typeof project.technologies_used === 'string' && project.technologies_used ? 
      project.technologies_used.split(',').map(t => t.trim()).filter(t => t) : []);
  
  const materials = Array.isArray(project.materials_used) ? 
    project.materials_used.filter(m => m) : 
    (typeof project.materials_used === 'string' && project.materials_used ? 
      project.materials_used.split(',').map(m => m.trim()).filter(m => m) : []);
  
  const gallery = Array.isArray(project.gallery_images) ? 
    project.gallery_images.filter(img => img) : 
    (typeof project.gallery_images === 'string' && project.gallery_images ? 
      JSON.parse(project.gallery_images) || [] : []);
  
  setFormData({
    project_code: project.project_code || '',
    project_name: project.project_name || '',
    project_type: project.project_type || '',
    client_name: project.client_name || '',
    client_email: project.client_email || '',
    client_phone: project.client_phone || '',
    short_description: project.short_description || '',
    detailed_description: project.detailed_description || '',
    location: project.location || '',
    budget: project.budget || '',
    currency: project.currency || 'USD',
    start_date: project.start_date ? formatDateForInput(project.start_date) : '',
    end_date: project.end_date ? formatDateForInput(project.end_date) : '',
    project_status: project.project_status || 'Planned',
    progress_percentage: project.progress_percentage || 0,
    project_manager: project.project_manager || '',
    team_size: project.team_size || '',
    technologies_used: technologies,
    materials_used: materials,
    thumbnail_image: project.thumbnail_image || '',
    gallery_images: gallery,
    is_featured: project.is_featured || false,
    is_active: project.is_active !== undefined ? project.is_active : true,
    thumbnail_file: null, // Reset file object
    gallery_files: [] // Reset file array
  });
  
  // Set thumbnail preview
  if (project.thumbnail_image) {
    const imageUrl = getImageUrl(project.thumbnail_image);
    console.log('Thumbnail URL:', imageUrl);
    setThumbnailPreview(imageUrl);
  } else {
    setThumbnailPreview(null);
  }
  
  // Set gallery previews
  if (gallery.length > 0) {
    const previews = gallery.map(img => getImageUrl(img)).filter(img => img);
    console.log('Gallery previews:', previews);
    setGalleryPreviews(previews);
  } else {
    setGalleryPreviews([]);
  }
  
  setShowModal(true);
};  
  const handleView = (project) => {
    setViewingProject(project);
    setShowViewModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
    resetForm();
  };
  
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingProject(null);
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const clearFilters = () => {
    setFilters({
      search: '',
      project_type: '',
      project_status: '',
      is_featured: '',
      is_active: ''
    });
  };
  
  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath === 'null' || imagePath === 'undefined' || imagePath === '' || imagePath === '[]') {
      return null;
    }
    
    // If it's already a full URL
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // If it starts with /, it's already a relative path
    if (imagePath.startsWith('/')) {
      return `${UPLOAD_BASE_URL}${imagePath}`;
    }
    
    // If it contains uploads folder
    if (imagePath.includes('uploads/')) {
      return `${UPLOAD_BASE_URL}/${imagePath}`;
    }
    
    // Default: assume it's in uploads/projects folder
    return `${UPLOAD_BASE_URL}/uploads/projects/${imagePath}`;
  };
  
  const getProjectIcon = (projectType) => {
    const icons = {
      'Real Estate': <FaBuilding />,
      'Construction': <FaHammer />,
      'Software Development': <FaLaptopCode />,
      'IT Services': <FaServer />,
      'Manufacturing': <FaIndustry />,
      'Consulting': <FaUserTie />,
      'Infrastructure': <FaRoad />,
      'Other': <FaCube />
    };
    return icons[projectType] || <FaCube />;
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      'Planned': 'info',
      'Ongoing': 'primary',
      'On Hold': 'warning',
      'Completed': 'success',
      'Cancelled': 'danger'
    };
    return badges[status] || 'secondary';
  };
  
  const getStatusIcon = (status) => {
    const icons = {
      'Planned': <FaCalendarPlus className="me-1" />,
      'Ongoing': <FaClock className="me-1" />,
      'On Hold': <FaPauseCircle className="me-1" />,
      'Completed': <FaCheckCircle className="me-1" />,
      'Cancelled': <FaTimesCircle className="me-1" />
    };
    return icons[status] || <FaCalendarAlt className="me-1" />;
  };
  
  const formatCurrency = (amount, currency) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0);
    } catch (error) {
      return `${currency || 'USD'} ${amount || 0}`;
    }
  };
  
  if (loading && activeTab === 'projects') {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading projects...</p>
        </Container>
      </>
    );
  }
  
  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Projects Manager</h2>
            <p className="text-muted">Manage your business projects portfolio</p>
          </Col>
          <Col className="text-end">
            <Button 
              variant="primary" 
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <FaPlus className="me-2" />
              Add Project
            </Button>
          </Col>
        </Row>
        
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
        
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
          <Tab eventKey="projects" title={
            <>
              <FaChartLine className="me-2" />
              Projects
              {projects.length > 0 && (
                <Badge bg="secondary" className="ms-2">{projects.length}</Badge>
              )}
            </>
          }>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label><FaSearch className="me-2" /> Search</Form.Label>
                      <Form.Control
                        type="text"
                        name="search"
                        placeholder="Search projects..."
                        value={filters.search}
                        onChange={handleFilterChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label><FaFilter className="me-2" /> Type</Form.Label>
                      <Form.Select
                        name="project_type"
                        value={filters.project_type}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Types</option>
                        {(options.project_types || []).map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label><FaFilter className="me-2" /> Status</Form.Label>
                      <Form.Select
                        name="project_status"
                        value={filters.project_status}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Status</option>
                        {(options.project_statuses || []).map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label><FaStar className="me-2" /> Featured</Form.Label>
                      <Form.Select
                        name="is_featured"
                        value={filters.is_featured}
                        onChange={handleFilterChange}
                      >
                        <option value="">All</option>
                        <option value="1">Featured</option>
                        <option value="0">Not Featured</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label><FaCheckCircle className="me-2" /> Active</Form.Label>
                      <Form.Select
                        name="is_active"
                        value={filters.is_active}
                        onChange={handleFilterChange}
                      >
                        <option value="">All</option>
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={1} className="d-flex align-items-end">
                    <Button 
                      variant="outline-secondary" 
                      onClick={clearFilters}
                      size="sm"
                    >
                      <FaTimes className="me-1" />
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            
            {!Array.isArray(projects) || projects.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <FaCube size={48} className="text-muted" />
                </div>
                <h4 className="text-muted mb-3">No Projects Found</h4>
                <p className="text-muted mb-4">Try adjusting your filters or add a new project</p>
                <Button 
                  variant="primary" 
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  <FaPlus className="me-2" />
                  Add Your First Project
                </Button>
              </div>
            ) : (
              <Row className="g-4 mb-4">
                {(filteredProjects || []).map(project => (
                  <Col lg={4} md={6} key={project.id}>
                    <Card className="h-100 border-0 shadow-sm project-card">
                      <div className="position-relative">
                        {project.thumbnail_image ? (
                          <Card.Img 
                            variant="top" 
                            src={getImageUrl(project.thumbnail_image)}
                            alt={project.project_name}
                            style={{ height: '200px', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentNode.innerHTML = `
                                <div class="project-placeholder d-flex align-items-center justify-content-center" 
                                     style="height: 200px; background-color: #f8f9fa">
                                  <div class="text-center">
                                    ${getProjectIcon(project.project_type)}
                                    <p class="mt-2 text-muted">Image not found</p>
                                  </div>
                                </div>
                              `;
                            }}
                          />
                        ) : (
                          <div className="project-placeholder d-flex align-items-center justify-content-center"
                               style={{ height: '200px', backgroundColor: '#f8f9fa' }}>
                            <div className="text-center">
                              {getProjectIcon(project.project_type)}
                              <p className="mt-2 text-muted">No Image</p>
                            </div>
                          </div>
                        )}
                        <div className="position-absolute top-0 end-0 m-2">
                          <Badge bg={getStatusBadge(project.project_status)}>
                            {getStatusIcon(project.project_status)}
                            {project.project_status}
                          </Badge>
                        </div>
                        {project.is_featured && (
                          <div className="position-absolute top-0 start-0 m-2">
                            <Badge bg="warning" text="dark">
                              <FaStar className="me-1" />
                              Featured
                            </Badge>
                          </div>
                        )}
                      </div>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <Card.Title className="mb-0">{project.project_name}</Card.Title>
                          <Badge bg="light" text="dark" className="ms-2">
                            {project.project_code}
                          </Badge>
                        </div>
                        <Card.Subtitle className="mb-2 text-muted">
                          <FaBuilding className="me-1" />
                          {project.project_type}
                        </Card.Subtitle>
                        
                        <p className="small text-muted mb-3">
                          {project.short_description || 'No description available'}
                        </p>
                        
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="text-muted">Progress</small>
                            <small className="text-muted">{project.progress_percentage}%</small>
                          </div>
                          <ProgressBar 
                            now={project.progress_percentage} 
                            variant={
                              project.progress_percentage >= 100 ? 'success' :
                              project.progress_percentage >= 50 ? 'primary' :
                              project.progress_percentage >= 25 ? 'warning' : 'info'
                            }
                            style={{ height: '6px' }}
                          />
                        </div>
                        
                        <div className="project-details mb-3">
                          <div className="row small text-muted">
                            <div className="col-6 mb-2">
                              <FaMapMarkerAlt className="me-1" />
                              {project.location || 'N/A'}
                            </div>
                            <div className="col-6 mb-2">
                              <FaUsers className="me-1" />
                              Team: {project.team_size || 0}
                            </div>
                            <div className="col-6 mb-2">
                              <FaCalendarAlt className="me-1" />
                              Start: {formatDate(project.start_date)}
                            </div>
                            <div className="col-6 mb-2">
                              <FaCalendarCheck className="me-1" />
                              End: {formatDate(project.end_date)}
                            </div>
                            <div className="col-12">
                              <FaMoneyBill className="me-1" />
                              Budget: {formatCurrency(project.budget, project.currency)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <Badge bg={project.is_active ? 'success' : 'danger'} className="me-2">
                              {project.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {project.gallery_images && project.gallery_images.length > 0 && (
                              <Badge bg="info">
                                <FaImage className="me-1" />
                                {project.gallery_images.length}
                              </Badge>
                            )}
                          </div>
                          <div>
                            <Button
                              size="sm"
                              variant="outline-info"
                              className="me-2"
                              onClick={() => handleView(project)}
                            >
                              <FaEye />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              onClick={() => handleEdit(project)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(project.id)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tab>
          
          <Tab eventKey="statistics" title={
            <>
              <FaChartBar className="me-2" />
              Statistics
            </>
          }>
            {statistics ? (
              <Row className="g-4">
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        <div className="bg-primary bg-opacity-10 text-primary p-3 rounded-circle d-inline-block">
                          <FaCube size={24} />
                        </div>
                      </div>
                      <h3 className="mb-0">{statistics.total || 0}</h3>
                      <p className="text-muted mb-0">Total Projects</p>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        <div className="bg-success bg-opacity-10 text-success p-3 rounded-circle d-inline-block">
                          <FaCheckCircle size={24} />
                        </div>
                      </div>
                      <h3 className="mb-0">{statistics.active || 0}</h3>
                      <p className="text-muted mb-0">Active Projects</p>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        <div className="bg-warning bg-opacity-10 text-warning p-3 rounded-circle d-inline-block">
                          <FaStar size={24} />
                        </div>
                      </div>
                      <h3 className="mb-0">{statistics.featured || 0}</h3>
                      <p className="text-muted mb-0">Featured Projects</p>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        <div className="bg-info bg-opacity-10 text-info p-3 rounded-circle d-inline-block">
                          <FaMoneyBill size={24} />
                        </div>
                      </div>
                      <h3 className="mb-0">
                        {formatCurrency(statistics.budget_summary?.total_budget || 0)}
                      </h3>
                      <p className="text-muted mb-0">Total Budget</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            ) : (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading statistics...</p>
              </div>
            )}
          </Tab>
        </Tabs>
        
        {/* Add/Edit Project Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingProject ? 'Edit Project' : 'Add New Project'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Tabs defaultActiveKey="basic" className="mb-3">
                <Tab eventKey="basic" title="Basic Info">
                  <Row className="mb-3">
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="project_name"
                          value={formData.project_name}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter project name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="project_code"
                          value={formData.project_code}
                          onChange={handleInputChange}
                          placeholder="Auto-generated"
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Type *</Form.Label>
                        <Form.Select
                          name="project_type"
                          value={formData.project_type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Type</option>
                          {(options.project_types || []).map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Status</Form.Label>
                        <Form.Select
                          name="project_status"
                          value={formData.project_status}
                          onChange={handleInputChange}
                        >
                          {(options.project_statuses || []).map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Short Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="short_description"
                      value={formData.short_description}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Brief description (max 500 characters)"
                      maxLength={500}
                    />
                    <Form.Text className="text-muted">
                      {formData.short_description?.length || 0}/500 characters
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Detailed Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="detailed_description"
                      value={formData.detailed_description}
                      onChange={handleInputChange}
                      rows={5}
                      placeholder="Detailed project description..."
                    />
                  </Form.Group>
                </Tab>
                
                <Tab eventKey="client" title="Client Info">
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Client Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="client_name"
                          value={formData.client_name}
                          onChange={handleInputChange}
                          placeholder="Client/Company name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <Form.Control
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="Project location"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Client Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="client_email"
                          value={formData.client_email}
                          onChange={handleInputChange}
                          placeholder="client@example.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Client Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          name="client_phone"
                          value={formData.client_phone}
                          onChange={handleInputChange}
                          placeholder="+1234567890"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab>
                
                <Tab eventKey="details" title="Project Details">
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="start_date"
                          value={formData.start_date}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="end_date"
                          value={formData.end_date}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Budget</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            name="budget"
                            value={formData.budget}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                          />
                          <Form.Select
                            name="currency"
                            value={formData.currency}
                            onChange={handleInputChange}
                            style={{ maxWidth: '100px' }}
                          >
                            {(options.currencies || ['USD', 'EUR', 'GBP']).map(currency => (
                              <option key={currency} value={currency}>{currency}</option>
                            ))}
                          </Form.Select>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Progress Percentage</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="range"
                            name="progress_percentage"
                            value={formData.progress_percentage}
                            onChange={handleInputChange}
                            min="0"
                            max="100"
                            step="1"
                            className="me-3"
                          />
                          <Badge bg="light" text="dark" className="fs-6">
                            {formData.progress_percentage}%
                          </Badge>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Manager</Form.Label>
                        <Form.Control
                          type="text"
                          name="project_manager"
                          value={formData.project_manager}
                          onChange={handleInputChange}
                          placeholder="Name of project manager"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Team Size</Form.Label>
                        <Form.Control
                          type="number"
                          name="team_size"
                          value={formData.team_size}
                          onChange={handleInputChange}
                          placeholder="Number of team members"
                          min="0"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Technologies Used</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., React, Node.js, MySQL (comma separated)"
                          onChange={(e) => handleArrayFieldChange('technologies_used', e.target.value)}
                          value={Array.isArray(formData.technologies_used) ? formData.technologies_used.join(', ') : formData.technologies_used}
                        />
                        <Form.Text className="text-muted">
                          {Array.isArray(formData.technologies_used) ? formData.technologies_used.length : 0} technologies listed
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Materials Used</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., Concrete, Steel, Glass (comma separated)"
                          onChange={(e) => handleArrayFieldChange('materials_used', e.target.value)}
                          value={Array.isArray(formData.materials_used) ? formData.materials_used.join(', ') : formData.materials_used}
                        />
                        <Form.Text className="text-muted">
                          {Array.isArray(formData.materials_used) ? formData.materials_used.length : 0} materials listed
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab>
                
                <Tab eventKey="media" title="Media">
                  <Row className="mb-4">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Thumbnail Image</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailChange}
                          disabled={uploading}
                        />
                        <Form.Text className="text-muted">
                          Recommended: 800x600px, max 2MB
                        </Form.Text>
                      </Form.Group>
                      
                      {thumbnailPreview && (
                        <div className="mt-3">
                          <Form.Label>Thumbnail Preview</Form.Label>
                          <div className="position-relative" style={{ maxWidth: '200px' }}>
                            <Image 
                              src={thumbnailPreview}
                              alt="Thumbnail preview" 
                              fluid 
                              rounded 
                              className="border"
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 m-1"
                              onClick={removeThumbnail}
                            >
                              <FaTimes />
                            </Button>
                          </div>
                        </div>
                      )}
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Gallery Images</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleGalleryChange}
                          disabled={uploading}
                        />
                        <Form.Text className="text-muted">
                          Multiple images allowed, max 5MB each
                        </Form.Text>
                      </Form.Group>
                      
                      {galleryPreviews.length > 0 && (
                        <div className="mt-3">
                          <Form.Label>Gallery Previews ({galleryPreviews.length})</Form.Label>
                          <Row className="g-2">
                            {galleryPreviews.map((preview, index) => (
                              <Col xs={4} key={index}>
                                <div className="position-relative">
                                  <Image 
                                    src={preview}
                                    alt={`Gallery ${index + 1}`} 
                                    fluid 
                                    rounded 
                                    className="border"
                                    style={{ height: '80px', objectFit: 'cover' }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    className="position-absolute top-0 end-0 m-1"
                                    onClick={() => removeGalleryImage(index)}
                                  >
                                    <FaTimes />
                                  </Button>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}
                    </Col>
                  </Row>
                </Tab>
                
                <Tab eventKey="settings" title="Settings">
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <div className="d-flex gap-3">
                          <Form.Check
                            type="switch"
                            id="is_active"
                            label="Active"
                            checked={formData.is_active}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                          />
                          <Form.Check
                            type="switch"
                            id="is_featured"
                            label="Featured"
                            checked={formData.is_featured}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                          />
                        </div>
                        <Form.Text className="text-muted">
                          Active projects are visible on the website. Featured projects are highlighted.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  {editingProject && (
                    <Alert variant="info">
                      <Alert.Heading>Project Information</Alert.Heading>
                      <div className="small">
                        <div className="d-flex justify-content-between">
                          <span>Created:</span>
                          <strong>{formatDateTime(editingProject.created_at)}</strong>
                        </div>
                        <div className="d-flex justify-content-between mt-1">
                          <span>Last Updated:</span>
                          <strong>{formatDateTime(editingProject.updated_at)}</strong>
                        </div>
                      </div>
                    </Alert>
                  )}
                </Tab>
              </Tabs>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} disabled={uploading}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploading}>
                {uploading && <Spinner animation="border" size="sm" className="me-2" />}
                {editingProject ? 'Update' : 'Create'} Project
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
        
        {/* View Project Modal */}
        <Modal show={showViewModal} onHide={handleCloseViewModal} size="xl" centered>
          {viewingProject && (
            <>
              <Modal.Header closeButton>
                <Modal.Title>
                  <div className="d-flex align-items-center">
                    {getProjectIcon(viewingProject.project_type)}
                    <div className="ms-3">
                      <h5 className="mb-0">{viewingProject.project_name}</h5>
                      <small className="text-muted">{viewingProject.project_code}</small>
                    </div>
                  </div>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                  <Col lg={8}>
                    {/* Image Carousel */}
                    {viewingProject.gallery_images && viewingProject.gallery_images.length > 0 ? (
                      <Carousel className="mb-4">
                        {viewingProject.gallery_images.map((image, index) => (
                          <Carousel.Item key={index}>
                            <Image
                              src={getImageUrl(image)}
                              alt={`${viewingProject.project_name} - Image ${index + 1}`}
                              fluid
                              className="rounded"
                              style={{ maxHeight: '400px', objectFit: 'cover', width: '100%' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = `
                                  <div class="d-flex align-items-center justify-content-center bg-light" 
                                       style="height: 400px; width: 100%">
                                    <div class="text-center">
                                      <FaImage size={48} className="text-muted mb-3" />
                                      <p class="text-muted">Image not found</p>
                                    </div>
                                  </div>
                                `;
                              }}
                            />
                          </Carousel.Item>
                        ))}
                      </Carousel>
                    ) : viewingProject.thumbnail_image ? (
                      <div className="mb-4">
                        <Image
                          src={getImageUrl(viewingProject.thumbnail_image)}
                          alt={viewingProject.project_name}
                          fluid
                          className="rounded"
                          style={{ maxHeight: '400px', objectFit: 'cover', width: '100%' }}
                        />
                      </div>
                    ) : (
                      <div className="mb-4 text-center py-5 bg-light rounded">
                        <FaCube size={48} className="text-muted mb-3" />
                        <p className="text-muted">No images available</p>
                      </div>
                    )}
                    
                    {/* Detailed Description */}
                    <Card className="mb-4">
                      <Card.Header>
                        <h6 className="mb-0">Project Details</h6>
                      </Card.Header>
                      <Card.Body>
                        <div className="project-description">
                          {viewingProject.detailed_description ? (
                            viewingProject.detailed_description.split('\n').map((paragraph, index) => (
                              <p key={index} className="mb-3">
                                {paragraph}
                              </p>
                            ))
                          ) : (
                            <p className="text-muted">No detailed description provided.</p>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                    
                    {/* Technologies & Materials */}
                    <Row>
                      <Col md={6}>
                        <Card className="mb-4">
                          <Card.Header>
                            <h6 className="mb-0">Technologies Used</h6>
                          </Card.Header>
                          <Card.Body>
                            {viewingProject.technologies_used && viewingProject.technologies_used.length > 0 ? (
                              <div className="d-flex flex-wrap gap-2">
                                {viewingProject.technologies_used.map((tech, index) => (
                                  <Badge key={index} bg="info" className="p-2">
                                    {tech}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <p className="text-muted mb-0">No technologies specified</p>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6}>
                        <Card>
                          <Card.Header>
                            <h6 className="mb-0">Materials Used</h6>
                          </Card.Header>
                          <Card.Body>
                            {viewingProject.materials_used && viewingProject.materials_used.length > 0 ? (
                              <div className="d-flex flex-wrap gap-2">
                                {viewingProject.materials_used.map((material, index) => (
                                  <Badge key={index} bg="secondary" className="p-2">
                                    {material}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <p className="text-muted mb-0">No materials specified</p>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Col>
                  
                  <Col lg={4}>
                    {/* Project Info Card */}
                    <Card className="sticky-top" style={{ top: '20px' }}>
                      <Card.Header>
                        <h6 className="mb-0">Project Information</h6>
                      </Card.Header>
                      <Card.Body>
                        <ListGroup variant="flush">
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Status</span>
                            <Badge bg={getStatusBadge(viewingProject.project_status)}>
                              {viewingProject.project_status}
                            </Badge>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Type</span>
                            <span>
                              {getProjectIcon(viewingProject.project_type)}
                              <span className="ms-2">{viewingProject.project_type}</span>
                            </span>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Progress</span>
                            <div className="text-end">
                              <div className="mb-1">{viewingProject.progress_percentage}%</div>
                              <ProgressBar 
                                now={viewingProject.progress_percentage} 
                                variant={
                                  viewingProject.progress_percentage >= 100 ? 'success' :
                                  viewingProject.progress_percentage >= 50 ? 'primary' :
                                  viewingProject.progress_percentage >= 25 ? 'warning' : 'info'
                                }
                                style={{ height: '6px', width: '100px' }}
                              />
                            </div>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Location</span>
                            <span>
                              <FaMapMarkerAlt className="me-1" />
                              {viewingProject.location || 'N/A'}
                            </span>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Start Date</span>
                            <span>
                              <FaCalendarAlt className="me-1" />
                              {formatDate(viewingProject.start_date)}
                            </span>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">End Date</span>
                            <span>
                              <FaCalendarCheck className="me-1" />
                              {formatDate(viewingProject.end_date)}
                            </span>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Budget</span>
                            <span className="fw-bold">
                              {formatCurrency(viewingProject.budget, viewingProject.currency)}
                            </span>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Team Size</span>
                            <span>
                              <FaUsers className="me-1" />
                              {viewingProject.team_size || 0} members
                            </span>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Project Manager</span>
                            <span>
                              <FaUser className="me-1" />
                              {viewingProject.project_manager || 'Not assigned'}
                            </span>
                          </ListGroup.Item>
                          
                          {viewingProject.client_name && (
                            <ListGroup.Item>
                              <h6 className="mb-2">Client Information</h6>
                              <div className="small">
                                <div className="d-flex align-items-center mb-1">
                                  <FaUser className="me-2 text-muted" />
                                  <span>{viewingProject.client_name}</span>
                                </div>
                                {viewingProject.client_email && (
                                  <div className="d-flex align-items-center mb-1">
                                    <FaEnvelope className="me-2 text-muted" />
                                    <span>{viewingProject.client_email}</span>
                                  </div>
                                )}
                                {viewingProject.client_phone && (
                                  <div className="d-flex align-items-center">
                                    <FaPhone className="me-2 text-muted" />
                                    <span>{viewingProject.client_phone}</span>
                                  </div>
                                )}
                              </div>
                            </ListGroup.Item>
                          )}
                          
                          <ListGroup.Item className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Visibility</span>
                            <div>
                              <Badge bg={viewingProject.is_active ? 'success' : 'danger'} className="me-2">
                                {viewingProject.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              {viewingProject.is_featured && (
                                <Badge bg="warning" text="dark">
                                  <FaStar className="me-1" />
                                  Featured
                                </Badge>
                              )}
                            </div>
                          </ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                      <Card.Footer>
                        <div className="small text-muted">
                          Created: {formatDateTime(viewingProject.created_at)}
                          <br />
                          Last Updated: {formatDateTime(viewingProject.updated_at)}
                        </div>
                      </Card.Footer>
                    </Card>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleCloseViewModal}>
                  Close
                </Button>
                <Button variant="primary" onClick={() => {
                  handleCloseViewModal();
                  handleEdit(viewingProject);
                }}>
                  <FaEdit className="me-2" />
                  Edit Project
                </Button>
              </Modal.Footer>
            </>
          )}
        </Modal>
      </Container>
    </>
  );
};

export default ProjectsManager;